import axios from 'axios'

export default {

    async getMedia(media_id) {
        return await axios.get(`/media/${media_id}`)
    },

    /**
     * Returns { cdn_url: string | null } — a browser-renderable PDF URL
     * for the canonical document, materialising it onto Edaptio S3 first
     * if the row predates the direct-S3 cutover (no cdn_url, bytes only
     * on Filestack/Kami). `null` means unrecoverable → caller falls back
     * to Kami. Drives EmbedPDF's recovery for old submissions.
     */
    async getCanonicalPdfUrl(media_id) {
        return await axios.get(`/media/${media_id}/canonical-pdf`)
    },

    async createDocumentExport(data) {
        return await axios.post('/media/directedit', data)
    },

    async getCourseVideos(group_id) {
        return await axios.get(`/media/videos/courses/${group_id}`)
    },

    async getDirectEditLink(media_id, options) {
        options = options || {}

        let url = `/media/directedit/${media_id}`

        const query = []

        if (options.force_new_session) query.push('force_new_session')
        if (options.view_only_mode) query.push('view_only_mode')
        if (options.view_as_user) query.push(`view_as_user=${options.view_as_user}`)

        return await axios.get(url + (query.length ? `?${query.join('&')}` : ''))
    },

    async loadVideoAudioProgression({ media_id }) {
        return await axios.get(`/media/progression/${media_id}`)
    },

    async loadVimeoVideoProgression({ video_id }) {
        return await axios.get(`/media/vimeo/progression/${video_id}`)
    },

    async loadYoutubeVideoProgression({ video_id }) {
        return await axios.get(`/media/youtube/progression/${video_id}`)
    },

    async moveFileToFolder({ folder_id, file_id }) {
        return await axios.put(`/media/move/${file_id}`, {
            folder_id : folder_id
        })
    },

    /**
     * @desc Remove file
     * @param {number} media_id
     * @param {number} material_element_type - Material element adaptive_object_type_id
     */
    async removeFile(media_id, material_element_type = 0) {
        return await axios.delete(`/media/${media_id}/${material_element_type}`)
    },

    async saveMedia({ media_id, data }) {
        return await axios.put(`/media/${media_id}`, data)
    },

    async saveVideoAudioProgression({ media_id, latest_watch_time, duration, max_watch_time, total_watch_time }) {
        return await axios.put(`/media/progression/${media_id}`, {
            duration : duration,
            max_watch_time : max_watch_time,
            latest_watch_time : latest_watch_time,
            total_watch_time : total_watch_time
        })
    },

    async saveVimeoVideoProgression({ video_id, latest_watch_time, duration, max_watch_time, total_watch_time }) {
        return await axios.put(`/media/vimeo/progression/${video_id}`, {
            duration : duration,
            max_watch_time : max_watch_time,
            latest_watch_time : latest_watch_time,
            total_watch_time : total_watch_time
        })
    },

    async saveYoutubeVideoProgression({ video_id, latest_watch_time, duration, max_watch_time, total_watch_time }) {
        return await axios.put(`/media/youtube/progression/${video_id}`, {
            duration : duration,
            max_watch_time : max_watch_time,
            latest_watch_time : latest_watch_time,
            total_watch_time : total_watch_time
        })
    },

    async storeNewFile(file) {
        return await axios.post('/media', file)
    },

    /**
     * EmbedPDF annotation persistence — Phase 1+2 of the EmbedPDF
     * Kami-replacement V1 plan (thoughts/shared/plans/
     * 2026-05-06-embedpdf-kami-replacement-v1.md). Backed by Laravel
     * AnnotationsController + the new media_annotations table.
     */
    async getAnnotations(media_id) {
        return await axios.get(`/media/${media_id}/annotations`)
    },

    /**
     * Recordings attached to a feedback PDF. Unlike getAnnotations — which is
     * group-owner/observer only — this is readable by the student the feedback
     * is addressed to, which is what lets the readonly viewer play a voice
     * note, video comment or screen recording inline instead of following the
     * baked "▶" link out of the PDF.
     */
    async getFeedbackRecordings(media_id) {
        return await axios.get(`/media/${media_id}/feedback-recordings`)
    },

    /**
     * Incremental per-annotation persistence. Each annotation is keyed by
     * a stable client `uid` (EmbedPDF id, or 'rich:'<id> for voiceNote
     * sidecars) so a single failed request can never wipe the document.
     */
    async upsertAnnotation(media_id, uid, { page, type, payload }) {
        return await axios.put(
            `/media/${media_id}/annotations/${encodeURIComponent(uid)}`,
            { page, type, payload },
        )
    },

    async deleteAnnotation(media_id, uid) {
        return await axios.delete(
            `/media/${media_id}/annotations/${encodeURIComponent(uid)}`,
        )
    },

    /**
     * Returns { committed_annotator: 'embedpdf' | 'kami' | null }.
     * Drives the side-by-side toggle in assignmentFeedbackWrapper —
     * `null` means the teacher hasn't yet committed work in either
     * annotator and the toggle is shown; 'embedpdf' or 'kami' means
     * the submission is locked to that surface.
     */
    async getAnnotatorState(media_id) {
        return await axios.get(`/media/${media_id}/annotator-state`)
    },
}
